'use client'

import { motion } from 'framer-motion'
import { Space_Grotesk, Outfit } from 'next/font/google'
import { BackgroundBeams } from '../../_components/_ui/background-beams'

const spaceGrotesk = Space_Grotesk({ 
  subsets: ['latin'],
  variable: '--font-space-grotesk',
  display: 'swap'
})

const outfit = Outfit({ 
  subsets: ['latin'],
  variable: '--font-outfit',
  display: 'swap'
})

const Privacy = () => {
    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10">
                {/* Hero Section */}
                <div className="pt-16 pb-8 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto">
                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="font-space-grotesk text-2xl sm:text-3xl font-bold text-white leading-tight"
                        >
                            Privacy Policy
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-sm text-gray-400 mt-2"
                        >
                            Effective Date: {new Date().toLocaleDateString()}
                        </motion.p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-8 border border-neutral-800 prose prose-invert max-w-none"
                    >
                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">Hey There! 👋 Let's Talk Privacy</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                Look, we get it - privacy policies are usually about as fun as watching paint dry. But this is important stuff, so we'll keep it real with you about how Autofoc.us handles (or rather, doesn't handle) your data.
                            </p>
                            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
                                <p className="text-sm text-blue-300 leading-relaxed">
                                    <strong>TL;DR:</strong> We're basically just a fancy link manager. We don't host your sensitive data - we just point to where it lives elsewhere. Think of us as a really organized bookmark system that you can share with others.
                                </p>
                            </div>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">1. Our "Less is More" Data Philosophy</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                Here's our approach: we store as little of your data as humanly possible. Why? Because:
                            </p>
                            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2 mb-4">
                                <li>We're not in the data hoarding business</li>
                                <li>It keeps our costs down (which means lower prices for you)</li>
                                <li>If we don't have it, no one can steal it from us 🤓</li>
                                <li>We sleep better at night</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">2. What We Actually Store</h2>
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-base font-medium text-gray-200 mb-2">The Basics:</h3>
                                    <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2">
                                        <li>Your email (for logging in)</li>
                                        <li>Your username (for your cool Autofoc.us URL <span className="text-xs text-gray-400"> (autofoc.us/yourreallycoolusername)</span>)</li>
                                        <li>Your password (encrypted, of course)</li>
                                        <li>Text content (stored basically as JSON object<span className="text-xs text-gray-400"> (sup nerds)</span>) for features like markdown blocks</li>
                                        <li>Links and references to your external content</li>
                                        <li>Block configuration and layout preferences</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-base font-medium text-gray-200 mb-2">What We Don't Store:</h3>
                                    <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2">
                                        <li>Your photos, videos, or files (these stay on your preferred platforms like Imgur, YouTube, etc.)</li>
                                        <li>Any binary files or media content (we only store links to where these live)</li>
                                        <li>Payment information (that's handled by our payment processor)</li>
                                        <li>Sensitive personal data</li>
                                        <li>Analytics data from your linked content (that stays with your content platforms)</li>
                                        <li>Your deepest secrets (..unless you pay us to host them)</li>
                                    </ul>
                                </div>
                                <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4">
                                    <p className="text-sm text-indigo-300 leading-relaxed">
                                        <strong>💡 How it works:</strong> When you add content to Autofoc.us, we store the minimal amount of data needed to display and organize your links. For example, if you add a YouTube video, we store the video ID and your display preferences, but the actual video stays on YouTube. This keeps our service fast, reliable, and cost-effective while letting you maintain control of your content.
                                    </p>
                                </div>
                                <div className="mt-6">
                                    <h3 className="text-base font-medium text-gray-200 mb-2">Summit Tier Data Hosting:</h3>
                                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 mb-4">
                                        <p className="text-sm text-purple-300 leading-relaxed">
                                            <strong>🚀 Summit Tier Feature:</strong> For $16.99/month, Summit tier subscribers can opt to host their content directly with us. This means we'll store your images, files, and other content on our servers, giving you a more integrated experience.
                                        </p>
                                        <ul className="text-sm text-purple-300 list-disc pl-6 space-y-2 mt-3">
                                            <li>Unlimited* content storage for active pages</li>
                                            <li>Content must be organized in block format on active Autofoc.us pages</li>
                                            <li>Data export available at any time</li>
                                            <li>While we can't promise eternal hosting, we commit to providing your data back if our service ever discontinues</li>
                                        </ul>
                                        <p className="text-xs text-purple-400 mt-3">
                                            *Unlimited storage is subject to fair use and active page requirements
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="mt-6">
                                    <h3 className="text-base font-medium text-gray-200 mb-2">Analytics Data:</h3>
                                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                                        <p className="text-sm text-blue-300 leading-relaxed mb-3">
                                            We collect and store basic analytics to help you understand how your content performs:
                                        </p>
                                        <ul className="text-sm text-blue-300 list-disc pl-6 space-y-2">
                                            <li>Block views (how many times each block is viewed)</li>
                                            <li>Profile visits (total and unique visitors to your profile)</li>
                                            <li>Page views (how often each of your pages is accessed)</li>
                                            <li>Basic visitor information (country, device type, referrer)</li>
                                        </ul>
                                        <p className="text-xs text-blue-400 mt-3">
                                            This data is anonymized and used solely for providing you (and whoever YOU choose) with insights about your content's reach and engagement.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">3. How We Use Your Data</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                We use your data to:
                            </p>
                            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2">
                                <li>Let you log in (kind of important)</li>
                                <li>Show your content links to people you choose to share with</li>
                                <li>Send you important updates about our service</li>
                                <li>Generate anonymous statistics about site usage</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">4. Our Long-Term Commitment</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                We're building Autofoc.us to be sustainable and user-focused. But we're also realistic - if things don't work out, we're committed to:
                            </p>
                            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2">
                                <li>Open-sourcing our code so you can host your own instance</li>
                                <li>Providing ample notice of any service changes</li>
                                <li>Helping you export your data (the little we have)</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">5. Your Rights (you got em here!)</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                You can:
                            </p>
                            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2">
                                <li>Request a copy of your data (it won't be a big file, we promise)</li>
                                <li>Delete your account and all associated data</li>
                                <li>Update or correct your information</li>
                                <li>Ask us questions about this policy without getting corporate-speak in response</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">6. Changes to This Policy</h2>
                            <p className="text-sm text-gray-300 leading-relaxed">
                                If we update this policy, we'll let you know via email. No surprises here.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-white mb-4">7. Get in Touch</h2>
                            <p className="text-sm text-gray-300 leading-relaxed">
                                Questions? Concerns? Dad jokes? Reach out to us at:<br />
                                Email: privacy@autofoc.us
                            </p>
                        </section>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}

export default Privacy 